'use strict'

/**
 * CSV assembly, serialization, and browser download for Master Lots.
 * Detail export starts as a flat NetSuite line-item sheet (paste-ready), then
 * appends settlement sections when present. Dashboard export stays sectioned
 * (availability + filtered lots).
 */

import {getDisplayUnit} from '@/util/refinery/weightDisplay'
import {catalogEntriesFromManifest} from './manifestCatalog'
import {
  catalogUnitWeightTroyOunces,
  manifestUnitGrossTroyOunces,
} from './manifestWeights'
import {catalogPurityFraction} from './catalogItems'
import {resolveInventoryLocation} from './catalogProductInventory'
import {METAL_KEYS} from './math'

const FORMULA_PREFIX = /^[=+\-@\t\r]/u
const WEIGHT_UNIT_LABEL = getDisplayUnit('lot').label

export const NETSUITE_LINE_ITEM_HEADERS = Object.freeze([
  'Document Number',
  'Internal ID',
  'SKU Only',
  'Display Name',
  'Metal Purity',
  'Metal OZ',
  'Qty',
  'Location Oz',
  'Location',
  'Item Rate',
  'Amount',
  'Description',
])

// Dashboard export carries availability summary plus filtered lot rows.
export const buildDashboardExportSections = ({availability, lotRows}) => {
  return [
    {
      title: 'Global eligible unallocated fine weight',
      headers: ['Metal', `Fine ${WEIGHT_UNIT_LABEL}`],
      rows: (availability || []).map(({metal, fineToz}) => [
        metal.toUpperCase(),
        roundCsvNumber(fineToz, 6),
      ]),
    },
    {
      title: 'Filtered master lots',
      headers: [
        'Code',
        'Partner',
        'Contract',
        'Status',
        'Sales order',
        `Gross weight ${WEIGHT_UNIT_LABEL}`,
        'Milestones',
        'Next action',
        ...METAL_KEYS.flatMap((metal) => [
          `${metal.toUpperCase()} purity %`,
          `${metal.toUpperCase()} fine ${WEIGHT_UNIT_LABEL}`,
        ]),
      ],
      rows: (lotRows || []).map((row) => [
        row.masterLotCode,
        row.partner,
        row.contract,
        row.status,
        row.salesOrderTranId,
        roundCsvNumber(row.grossWeightToz, 3),
        row.milestoneProgress,
        row.nextAction,
        ...METAL_KEYS.flatMap((metal) => [
          roundCsvNumber(row.byMetal?.[metal]?.purityPct, 6),
          roundCsvNumber(row.byMetal?.[metal]?.fineToz ?? 0, 6),
        ]),
      ]),
    },
  ]
}

/** Detail CSV: untitled NetSuite line items first, then settlement appendix when present. */
export const buildMasterLotExportSections = (masterLot) => {
  const sections = [{
    headers: [...NETSUITE_LINE_ITEM_HEADERS],
    rows: buildMasterLotNetsuiteLineItemRows(masterLot),
  }]
  if (masterLot?.settlement) sections.push(...settlementSections(masterLot.settlement))
  return sections
}

export const serializeCsvSections = (sections) =>
  (sections || [])
    .map((section) => [
      ...(section.title ? [serializeRow([section.title])] : []),
      serializeRow(section.headers || []),
      ...(section.rows || []).map(serializeRow),
    ].join('\r\n'))
    .join('\r\n\r\n')

export const downloadCsv = (filename, sections) => {
  const blob = new Blob([serializeCsvSections(sections)], {type: 'text/csv;charset=utf-8'})
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = filename
  try {
    link.click()
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

/** `{netsuiteInternalId} - UCG {state}`; id-only snapshots emit the bare id. */
const netsuiteLocationLabel = (location) => {
  const resolved = resolveInventoryLocation(location)
  return resolved.state
    ? `${resolved.netsuiteInternalId} - UCG ${resolved.state}`
    : resolved.netsuiteInternalId
}

/** Flat NetSuite line-item rows for one master lot (source bars + catalog). */
const buildMasterLotNetsuiteLineItemRows = (masterLot) => {
  const manifest = masterLot?.manifest || {}
  const documentNumber = masterLot?.salesOrder?.tranId || 'RFSO'
  const primaryMetal = resolvePrimaryMetal(masterLot)
  const inventoryLocation = masterLot?.inventoryLocation || null
  return [
    ...(manifest.sourceUnits || []).map((unit) => sourceNetsuiteLineItemRow(unit, {
      documentNumber,
      primaryMetal,
      inventoryLocation,
    })),
    ...catalogEntriesFromManifest(manifest).map((entry) => catalogNetsuiteLineItemRow(entry, {
      documentNumber,
      inventoryLocation,
    })),
  ]
}

const settlementSections = (settlement) => [
  {
    title: 'Settlement metals',
    headers: ['Metal', 'Purity %', 'Metal value USD', `Credited fine ${WEIGHT_UNIT_LABEL}`, 'Refining fee USD'],
    rows: Object.entries(settlement.perMetal || {})
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([metal, row]) => [
        metal.toUpperCase(),
        roundCsvNumber(row.purityPct, 6),
        roundCsvNumber(row.metalValueUsd, 2),
        roundCsvNumber(row.creditedFineToz, 6),
        roundCsvNumber(row.refiningFeeUsd, 2),
      ]),
  },
  {
    title: 'Settlement fees',
    headers: ['Type', 'Description', 'Amount USD'],
    rows: [
      ['Refining fees', '', roundCsvNumber(settlement.totalRefiningFeeUsd, 2)],
      ['Contract fees', '', roundCsvNumber(settlement.totalContractFeesUsd, 2)],
      ['Ad hoc fees', '', roundCsvNumber(settlement.totalAdHocFeesUsd, 2)],
    ],
  },
  {
    title: 'Settlement totals',
    headers: ['Field', 'Amount USD'],
    rows: [
      ['Gross metal cash', roundCsvNumber(settlement.grossMetalCashUsd, 2)],
      ['Net cash', roundCsvNumber(settlement.netCashUsd, 2)],
    ],
  },
]

const resolvePrimaryMetal = (masterLot) => {
  const contract = masterLot?.contract || {}
  const candidate = contract.tierQualifyingMetal
    || (Array.isArray(contract.primaryMetals) && contract.primaryMetals[0])
    || 'au'
  return String(candidate).toLowerCase()
}

/**
 * Melted source material: gross troy ounces in `Qty`, fine ounces in
 * `Metal OZ`. Purity stays on `Metal Purity` (0–1). Do not copy the
 * fraction into `Metal OZ` — that column is ounces.
 *
 * Line items carry a single Metal Purity / Metal OZ pair (contract primary
 * metal). Multi-metal purity-as-columns is a different export shape.
 *
 * Display Name = serial/SKU for source units — there is no separate product
 * display name; serial is the canonical label (see sourceUnitIdentity).
 */
const sourceNetsuiteLineItemRow = (unit, {documentNumber, primaryMetal, inventoryLocation}) => {
  const gross = manifestUnitGrossTroyOunces(unit)
  const purityFraction = purityPercentToFraction(unit?.purity?.[primaryMetal])
  const fineOz = multiplyNullable(gross, purityFraction)
  const sku = unit?.sku || unit?.serialNumber || null
  return [
    documentNumber,
    // PG does not fabricate source item internal ids (serial = SKU).
    null,
    sku,
    sku,
    roundCsvNumber(purityFraction, 6),
    roundCsvNumber(fineOz, 6),
    roundCsvNumber(gross, 6),
    roundCsvNumber(fineOz, 6),
    netsuiteLocationLabel(unit?.location ?? inventoryLocation),
    0,
    0,
    unit?.notes || null,
  ]
}

const catalogNetsuiteLineItemRow = (entry, {documentNumber, inventoryLocation}) => {
  const qty = Number.isFinite(entry?.quantity) && entry.quantity > 0 ? entry.quantity : 1
  const metalKey = entry?.metalKey || null
  const purityFraction = catalogPurityFraction(entry, metalKey)
  const unitGross = catalogUnitWeightTroyOunces(entry)
  const explicitMetalOz = Number(entry?.metalOz)
  const metalOz = Number.isFinite(explicitMetalOz)
    ? explicitMetalOz
    : multiplyNullable(unitGross, purityFraction)
  return [
    documentNumber,
    entry?.productNetsuiteInternalId || entry?.netsuite_internalId || null,
    entry?.sku || null,
    entry?.name || entry?.displayName || entry?.description || null,
    roundCsvNumber(purityFraction, 6),
    roundCsvNumber(metalOz, 6),
    qty,
    roundCsvNumber(multiplyNullable(qty, metalOz), 6),
    netsuiteLocationLabel(entry?.locationSnapshot ?? inventoryLocation),
    0,
    0,
    entry?.notes || null,
  ]
}

/** FE store purity is percent 0–100; NetSuite line items want fraction 0–1. */
const purityPercentToFraction = (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return null
  if (value <= 1) return value
  return value / 100
}

const multiplyNullable = (left, right) => {
  if (typeof left !== 'number' || !Number.isFinite(left)) return null
  if (typeof right !== 'number' || !Number.isFinite(right)) return null
  return left * right
}

const roundCsvNumber = (value, digits) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null
  const factor = 10 ** digits
  return Math.round((value + Number.EPSILON) * factor) / factor
}

const serializeRow = (cells) => cells.map(serializeCell).join(',')

const serializeCell = (value) => {
  if (value == null) return ''
  let text = String(value)
  if (typeof value === 'string' && FORMULA_PREFIX.test(text)) text = `'${text}`
  return /[",\r\n]/u.test(text) ? `"${text.replace(/"/gu, '""')}"` : text
}

export const __testing__ = Object.freeze({
  buildMasterLotNetsuiteLineItemRows,
  netsuiteLocationLabel,
})
